// keyrd_console/src/components/AlertsPanel.js
import React, { useEffect, useMemo, useState } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function formatDateTime(value) {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toISOString().replace("T", " ").replace("Z", "Z");
  } catch {
    return String(value);
  }
}

function badgeStyle(kindRaw) {
  const kind = String(kindRaw || "").toLowerCase();

  const base = {
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.2,
  };

  if (kind === "safety_freeze" || kind === "registry_mismatch") {
    return {
      ...base,
      backgroundColor: "#fef2f2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }

  if (kind === "drift_bm_anomaly") {
    return {
      ...base,
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }

  return {
    ...base,
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #e5e7eb",
  };
}

function AlertsPanel({ auth }) {
  const { baseUrl, token, authorized, registryVersion, registryHash } = auth;

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filterKind, setFilterKind] = useState("all");

  useEffect(() => {
    if (!authorized || !baseUrl) return;

    const ac = new AbortController();

    setLoading(true);
    setErr(null);

    const headers = buildKeyrdHeaders({
      token,
      registryVersion,
      registryHash,
      extra: { Accept: "application/json" },
    });

    const readJson = async (res) => {
      const text = await res.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        throw new Error(`Non-JSON response: ${text.slice(0, 160)}…`);
      }

      if (!res.ok) {
        const msg = json?.error || json?.reason || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      return json;
    };

    fetch(`${baseUrl}/console/alerts`, { headers, signal: ac.signal })
      .then(async (res) => {
        const json = await readJson(res);

        // Contract: prefer { ok:true, alerts:[...] }, but tolerate legacy shapes.
        const list = Array.isArray(json?.alerts)
          ? json.alerts
          : Array.isArray(json)
          ? json
          : [];

        setAlerts(list);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setErr(e?.message || String(e));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [authorized, baseUrl, token, registryVersion, registryHash]);

  const filteredAlerts = useMemo(() => {
    const norm = (k) => String(k || "").toLowerCase();
    if (filterKind === "all") return alerts;
    return alerts.filter((a) => norm(a?.kind || a?.type) === filterKind);
  }, [alerts, filterKind]);

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
        Alerts
      </h1>

      <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 16 }}>
        Stream of drift and safety signals generated by the KeyRD controller and
        monitoring layer. Backed by <code>/console/alerts</code>.
      </p>

      {!authorized && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px solid #f97316",
            backgroundColor: "#fffbeb",
            color: "#7c2d12",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <strong>Authorization required.</strong>
          <br />
          Paste a valid RDN Console bearer token in the top bar and click{" "}
          <strong>Check RDN</strong> to view alerts.
        </div>
      )}

      {authorized && err && (
        <div
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            fontSize: 12,
            color: "#b91c1c",
            marginBottom: 12,
          }}
        >
          Error loading alerts: {err}
        </div>
      )}

      {authorized && !err && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <label style={{ fontSize: 12, color: "#4b5563" }}>
            Filter by kind:
          </label>
          <select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            style={{
              fontSize: 12,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #d1d5db",
              backgroundColor: "#fff",
            }}
          >
            <option value="all">All</option>
            <option value="drift_bm_anomaly">Drift / BM anomalies</option>
            <option value="safety_freeze">Safety freezes</option>
            <option value="registry_mismatch">Registry mismatches</option>
          </select>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Showing {filteredAlerts.length} of {alerts.length} alerts
          </div>
        </div>
      )}

      {authorized && loading && (
        <div style={{ fontSize: 13, color: "#6b7280" }}>Loading alerts…</div>
      )}

      {authorized && !loading && !err && alerts.length === 0 && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px dashed #9ca3af",
            backgroundColor: "#f9fafb",
            fontSize: 13,
          }}
        >
          No alerts yet. The monitoring layer has not detected drift anomalies
          or safety / registry issues for this pilot.
        </div>
      )}

      {authorized && !loading && !err && alerts.length > 0 && (
        <div
          style={{
            borderRadius: 10,
            backgroundColor: "#ffffff",
            boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12,
            }}
          >
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <th style={{ padding: "8px 10px", width: "20%" }}>Time</th>
                <th style={{ padding: "8px 10px", width: "18%" }}>Kind</th>
                <th style={{ padding: "8px 10px", width: "12%" }}>Severity</th>
                <th style={{ padding: "8px 10px", width: "18%" }}>Patient</th>
                <th style={{ padding: "8px 10px", width: "32%" }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map((a, idx) => {
                const kind = a?.kind || a?.type || "-";
                const kindNorm = String(kind).toLowerCase();
                const isRed =
                  kindNorm === "safety_freeze" || kindNorm === "registry_mismatch";

                return (
                  <tr
                    key={a?.id ?? `${kindNorm}-${idx}`}
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      backgroundColor: isRed ? "#fef2f2" : "transparent",
                    }}
                  >
                    <td style={{ padding: "8px 10px" }}>
                      {formatDateTime(a?.created_at || a?.detected_at)}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <span style={badgeStyle(kind)}>{kind}</span>
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {a?.severity || a?.level || "-"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {a?.patient_id || a?.patient || "-"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {a?.message || a?.description || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AlertsPanel;
